import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, Edit, Trash2, Camera, FileSpreadsheet, Download, Loader2, Layers, History, ToggleRight, IndianRupee, Eye } from 'lucide-react';
import BulkUploadDialog from '@/components/BulkUploadDialog';
import ProductBulkStatusDialog from '@/components/ProductBulkStatusDialog';
import ProductForm from '@/components/ProductForm';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsEnhanced = () => {
  const { canCreate, canEdit, canDelete } = useModulePermissions();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subBrands, setSubBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [subMenuOptions, setSubMenuOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  // Multi-image upgrade (Feb 2026): gallery is the saved product_images[] for an
  // existing product; pendingImageFiles is the queue of File objects selected for
  // a NEW product that will be uploaded right after the product is created.
  const [gallery, setGallery] = useState([]);
  const [pendingImageFiles, setPendingImageFiles] = useState([]);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [mrpHistory, setMrpHistory] = useState(null); // {product, rows}
  const [grnQuickView, setGrnQuickView] = useState(null); // {product, rows, loading}
  const [userRole, setUserRole] = useState('user');
  const [exporting, setExporting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  // SKU lifecycle filter — 'all' | 'active' | 'inactive'. Default 'all'
  // (keeps existing behaviour for users who never touched the new feature).
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusSummary, setStatusSummary] = useState({ total: 0, active: 0, inactive: 0 });

  const initialFormData = {
    name: '', description: '', product_image: '', barcodes: [],
    brand: '', sub_brand: '', category: '', menu_option: '', sub_menu: '',
    measurement_unit: 'PCS', case_quantity: '', bag_size: '', mrp: '',
    inward_price_incl_gst: '', inward_price_excl_gst: '',
    outward_selling_price: '', hsn_code: '', gst_percentage: '18',
    min_stock_level: '10', rack_location: '',
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchData();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'user');
      }
    } catch (e) {}
    // Initial load only — no re-fetch on re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [productsRes, brandsRes, categoriesRes, menuRes, summaryRes] = await Promise.all([
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/brands`, { headers }),
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/menu-options`, { headers }).catch(() => ({ data: { menu_options: [] } })),
        axios.get(`${API}/products/status-summary`, { headers }).catch(() => ({ data: { total: 0, active: 0, inactive: 0 } })),
      ]);
      setProducts(productsRes.data);
      setBrands(brandsRes.data.brands || []);
      // /api/categories was upgraded (Feb 2026) to return objects with OG metadata.
      // For this page we just need the name strings — coerce safely so legacy
      // string responses still work.
      const rawCats = categoriesRes.data.categories || [];
      const cats = rawCats.map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean);
      setCategories(cats);
      setMenuOptions(menuRes.data.menu_options || []);
      setStatusSummary(summaryRes.data || { total: 0, active: 0, inactive: 0 });
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  /** Flip ONE product's status. Optimistically updates the local list +
   * counters; reverts on API failure. Admin/manager only — backend rejects
   * other roles with 403. */
  const handleToggleStatus = async (product) => {
    const currentStatus = (product.status || 'active').toLowerCase();
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const verb = nextStatus === 'active' ? 'activate' : 'deactivate';
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} "${product.name}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/products/${product.id}/status`,
        { status: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Optimistic local update
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, status: nextStatus } : p)));
      setStatusSummary((prev) => ({
        total: prev.total,
        active: prev.active + (nextStatus === 'active' ? 1 : -1),
        inactive: prev.inactive + (nextStatus === 'inactive' ? 1 : -1),
      }));
      toast.success(`${product.name} → ${nextStatus.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${verb} product`);
    }
  };

  /** Flip the "Show on OG App" visibility for ONE product. Calls the OG
   * publishing endpoint and optimistically updates the row. */
  const handleToggleOGApp = async (product) => {
    const current = !!product.show_on_og_app;
    const next = !current;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API}/admin/og-publishing/${product.id}`,
        { show_on_og_app: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProducts((prev) => prev.map((p) => (p.id === product.id
        ? { ...p,
            show_on_og_app: data.show_on_og_app,
            og_app_selling_price: data.og_app_selling_price,
            og_published_at: data.og_published_at,
            og_last_updated_at: data.og_last_updated_at,
          }
        : p)));
      toast.success(`${product.name} → OG App ${next ? 'PUBLISHED' : 'HIDDEN'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update OG App visibility');
    }
  };

  const fetchSubBrands = async (brandName) => {
    if (!brandName) { setSubBrands([]); return; }
    try {
      const token = localStorage.getItem('token');
      const brandsRes = await axios.get(`${API}/masters/brands`, { headers: { Authorization: `Bearer ${token}` } });
      const brand = brandsRes.data.brands.find(b => b.name.toLowerCase() === brandName.toLowerCase());
      if (brand) {
        const subBrandsRes = await axios.get(`${API}/masters/sub-brands/by-brand/${brand.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSubBrands(subBrandsRes.data.sub_brands.map(sb => sb.name));
      } else {
        setSubBrands([]);
      }
    } catch (error) {
      setSubBrands([]);
    }
  };

  const handleBrandChange = (newBrand) => {
    setFormData({ ...formData, brand: newBrand, sub_brand: '' });
    fetchSubBrands(newBrand);
  };

  const fetchSubMenus = async (menuGroupName) => {
    if (!menuGroupName) { setSubMenuOptions([]); return; }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const menuRes = await axios.get(`${API}/masters/menu`, { headers });
      const menuGroup = (menuRes.data.menu_items || []).find(m => m.name.toLowerCase() === menuGroupName.toLowerCase());
      if (menuGroup) {
        const subMenuRes = await axios.get(`${API}/masters/sub-menu/by-menu-group/${menuGroup.id}`, { headers });
        setSubMenuOptions((subMenuRes.data.sub_menus || []).map(sm => sm.name));
      } else {
        setSubMenuOptions([]);
      }
    } catch (error) {
      setSubMenuOptions([]);
    }
  };

  const handleMenuChange = (newMenu) => {
    fetchSubMenus(newMenu);
  };

  const canManageProducts = ['admin', 'manager'].includes(userRole);
  const canExport = ['admin', 'manager', 'accountant'].includes(userRole);

  const handleExport = async (exportSelected = false) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (exportSelected && selectedProducts.length > 0) payload.product_ids = selectedProducts;
      else if (searchTerm) payload.search = searchTerm;
      const response = await axios.post(`${API}/export/catalogue`, payload, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Catalogue_Export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportSelected ? selectedProducts.length : filteredProducts.length} products`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleExportHistory = async (product) => {
    const key = product.product_id || product.id;
    if (!key) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products/${encodeURIComponent(key)}/transaction-history.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeCode = (product.product_id || product.id || 'product').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.setAttribute('download', `product_transactions_${safeCode}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Transaction history exported for ${product.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export transaction history');
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const toggleSelectAll = () => {
    setSelectedProducts(selectedProducts.length === filteredProducts.length ? [] : filteredProducts.map(p => p.id));
  };

  const handleBulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;
    if (!window.confirm(`Delete ${selectedProducts.length} product(s)? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(API + '/products/bulk-delete', { ids: selectedProducts }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${selectedProducts.length} product(s) deleted`);
      setSelectedProducts([]);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Product deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const uploadPendingImages = async (productInternalId, headers) => {
    // Used after a NEW product is created to push the queued File objects from the
    // form into the gallery endpoint. Failures are toasted but don't roll back the
    // product create — the user can re-open the form and add images later.
    if (!pendingImageFiles || pendingImageFiles.length === 0) return;
    try {
      const form = new FormData();
      pendingImageFiles.forEach((p) => form.append('files', p.file));
      await axios.post(
        `${API}/admin/products/${productInternalId}/images`, form,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
    } catch (e) {
      toast.error(`Product saved but ${pendingImageFiles.length} image upload(s) failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        name: formData.name, description: formData.description, product_image: formData.product_image,
        barcodes: formData.barcodes, barcode: formData.barcodes[0] || null,
        brand: formData.brand, sub_brand: formData.sub_brand, category: formData.category,
        menu_option: formData.menu_option, measurement_unit: formData.measurement_unit,
        case_quantity: formData.case_quantity ? parseInt(formData.case_quantity) : null,
        bag_size: ['KG', 'BAG'].includes((formData.measurement_unit || '').toUpperCase()) && formData.bag_size ? parseFloat(formData.bag_size) : null,
        mrp: parseFloat(formData.mrp), inward_price_incl_gst: parseFloat(formData.inward_price_incl_gst),
        outward_selling_price: parseFloat(formData.outward_selling_price), hsn_code: formData.hsn_code,
        gst_percentage: parseFloat(formData.gst_percentage), min_stock_level: parseInt(formData.min_stock_level),
        rack_location: formData.rack_location,
      };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, { headers });
        toast.success('Product updated successfully');
      } else {
        const created = await axios.post(`${API}/products`, payload, { headers });
        const newId = created.data?.id;
        if (newId && pendingImageFiles.length > 0) {
          await uploadPendingImages(newId, headers);
        }
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    // Use nullish coalescing so legitimate 0% GST is preserved (vs. `|| 18`
    // which would silently overwrite it).
    const gstPct = product.gst_percentage ?? 18;
    const gstRate = gstPct / 100;
    let inclPrice = product.inward_price_incl_gst;
    let exclPrice = product.inward_price_excl_gst || product.inward_b2b_price;
    if (!inclPrice && exclPrice) inclPrice = exclPrice * (1 + gstRate);
    if (!exclPrice && inclPrice) exclPrice = inclPrice / (1 + gstRate);
    setFormData({
      name: product.name, description: product.description || '', product_image: product.product_image || '',
      barcodes: product.barcodes || (product.barcode ? [product.barcode] : []),
      brand: product.parent_brand || product.brand || '', sub_brand: product.sub_brand || '',
      category: product.category, menu_option: product.menu_option || '', sub_menu: product.sub_menu || '',
      measurement_unit: product.measurement_unit || product.unit || 'PCS',
      case_quantity: product.case_quantity?.toString() || '', bag_size: product.bag_size?.toString() || '', mrp: product.mrp?.toString() || '',
      inward_price_incl_gst: inclPrice?.toFixed(2) || '', inward_price_excl_gst: exclPrice?.toFixed(2) || '',
      outward_selling_price: product.outward_selling_price?.toString() || '', hsn_code: product.hsn_code,
      gst_percentage: gstPct.toString(), min_stock_level: product.min_stock_level?.toString() || '10',
      rack_location: product.rack_location || '',
    });
    const brandName = product.parent_brand || product.brand;
    if (brandName) fetchSubBrands(brandName);
    if (product.menu_option) fetchSubMenus(product.menu_option);
    setGallery(Array.isArray(product.product_images) ? product.product_images : []);
    setPendingImageFiles([]);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSubBrands([]);
    setSubMenuOptions([]);
    setGallery([]);
    // Revoke any preview URLs queued from a previous open to avoid memory leaks.
    pendingImageFiles.forEach((p) => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
    setPendingImageFiles([]);
  };

  const filteredProducts = products.filter(p => {
    // Status filter — 'all' shows everything, 'active' hides inactive,
    // 'inactive' hides active.
    const pStatus = (p.status || 'active').toLowerCase();
    if (statusFilter === 'active' && pStatus !== 'active') return false;
    if (statusFilter === 'inactive' && pStatus !== 'inactive') return false;
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return p.name.toLowerCase().includes(term) ||
      (p.product_id && p.product_id.toLowerCase().includes(term)) ||
      (p.sku && p.sku.toLowerCase().includes(term)) ||
      p.category.toLowerCase().includes(term) ||
      (p.barcode && p.barcode.toLowerCase().includes(term)) ||
      (Array.isArray(p.barcodes) && p.barcodes.some(bc => bc.toLowerCase().includes(term)));
  });

  if (loading) return <div className="flex items-center justify-center h-full">Loading products...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">Master Catalogue</h1>
          <p className="text-slate-600 mt-1">Manage your product inventory with enhanced details</p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && selectedProducts.length > 0 && (
            <>
            <Button variant="outline" onClick={() => handleExport(true)} disabled={exporting} className="gap-2">
              {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
              Export Selected ({selectedProducts.length})
            </Button>
            <CsvDownloadButton onDownload={() => handleExport(true)} testId="catalogue-export-selected-csv" size="default" disabled={exporting} />
            </>
          )}
          {selectedProducts.length > 0 && (
            <Button variant="outline" onClick={handleBulkDeleteProducts} className="gap-2 border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-selected-products">
              <Trash2 size={18} />
              Delete Selected ({selectedProducts.length})
            </Button>
          )}
          {canExport && (
            <>
            <Button variant="outline" onClick={() => handleExport(false)} disabled={exporting} className="gap-2">
              {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
              Export to Excel
            </Button>
            <CsvDownloadButton onDownload={() => handleExport(false)} testId="catalogue-export-csv" size="default" disabled={exporting} />
            </>
          )}
          {canManageProducts && (
            <Button variant="outline" onClick={() => setBulkStatusOpen(true)} className="gap-2" data-testid="bulk-status-btn">
              <ToggleRight size={18} /> Bulk Status
            </Button>
          )}
          {canManageProducts && (
            <Button variant="outline" onClick={() => setBulkUploadOpen(true)} className="gap-2">
              <FileSpreadsheet size={18} /> Bulk Upload
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingProduct(null); resetForm(); } }}>
            {canCreate('products') && (
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-product-btn"><Plus size={18} /> Add Product</Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <ProductForm
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                onCancel={() => setDialogOpen(false)}
                editingProduct={editingProduct}
                brands={brands}
                subBrands={subBrands}
                categories={categories}
                menuOptions={menuOptions}
                subMenuOptions={subMenuOptions}
                onBrandChange={handleBrandChange}
                onMenuChange={handleMenuChange}
                gallery={gallery}
                setGallery={setGallery}
                pendingImageFiles={pendingImageFiles}
                setPendingImageFiles={setPendingImageFiles}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="sku-status-summary">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Products</div>
            <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="kpi-total">{statusSummary.total}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/30 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Active</div>
            <div className="text-3xl font-bold text-emerald-700 mt-1" data-testid="kpi-active">{statusSummary.active}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-300 bg-slate-50/40 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Inactive</div>
            <div className="text-3xl font-bold text-slate-600 mt-1" data-testid="kpi-inactive">{statusSummary.inactive}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input placeholder="Search by name, Product ID, barcode, or category..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="product-search-input" />
            </div>
            <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 p-1" data-testid="sku-status-filter">
              {['all', 'active', 'inactive'].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setStatusFilter(opt)}
                  className={`px-3 py-1.5 text-xs rounded-md uppercase tracking-wider font-semibold transition-colors ${
                    statusFilter === opt
                      ? opt === 'active'
                        ? 'bg-emerald-600 text-white'
                        : opt === 'inactive'
                        ? 'bg-slate-700 text-white'
                        : 'bg-blue-600 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`status-pill-${opt}`}
                >
                  {opt === 'all' ? `All (${statusSummary.total})`
                    : opt === 'active' ? `Active (${statusSummary.active})`
                    : `Inactive (${statusSummary.inactive})`}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[calc(100vh-14rem)]" data-testid="catalogue-table-scroll">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]" data-testid="catalogue-table-head">
                <tr className="border-b border-slate-200 bg-slate-50">
                  {canExport && (
                    <th className="py-3 px-4 w-10">
                      <input type="checkbox" checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0} onChange={toggleSelectAll} className="rounded border-slate-300" />
                    </th>
                  )}
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Product ID</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Image</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Name / Parent Brand</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Category</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Case Qty</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">MRP</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Selling Price<span className="block normal-case tracking-normal text-[10px] font-normal">(Incl. GST)</span></th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Stock</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Rack</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Bin</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Status</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" title="Show on OG Retailer App">OG App</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedProducts.includes(product.id) ? 'bg-blue-50' : ''}`}>
                    {canExport && (
                      <td className="py-3 px-4">
                        <input type="checkbox" checked={selectedProducts.includes(product.id)} onChange={() => toggleProductSelection(product.id)} className="rounded border-slate-300" />
                      </td>
                    )}
                    <td className="py-3 px-4 font-mono text-xs">{product.product_id || product.sku}</td>
                    <td className="py-3 px-4">
                      {product.product_image ? (
                        <img src={`${BACKEND_URL}${product.product_image}`} alt={product.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center text-slate-400"><Camera size={20} /></div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-slate-500">{product.parent_brand || product.brand}</p>
                        {product.created_by_name && (
                          <p className="text-xs text-slate-400 mt-0.5" data-testid={'product-created-by-' + product.id}>Added by: {product.created_by_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">{product.category}</td>
                    <td className="py-3 px-4 text-center font-mono">{product.case_quantity || '-'}</td>
                    <td className="py-3 px-4 text-right font-mono">₹{(product.mrp || 0).toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-mono" data-testid={'product-selling-price-' + product.id}>
                      {product.outward_selling_price ? `₹${Number(product.outward_selling_price).toFixed(2)}` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-mono font-semibold ${product.current_stock < product.min_stock_level ? 'text-amber-600' : 'text-green-600'}`}>
                        {product.current_stock} {product.measurement_unit}
                        {product.bag_size ? <span className="block text-[10px] text-slate-400">Bag: {product.bag_size} KG</span> : null}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-slate-700" data-testid={'product-rack-' + product.id}>
                      {product.rack_location || '-'}
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-slate-700" data-testid={'product-bin-' + product.id}>
                      {product.bin || '-'}
                    </td>
                    <td className="py-3 px-4 text-center" data-testid={'product-status-cell-' + product.id}>
                      {(() => {
                        const isActive = (product.status || 'active').toLowerCase() === 'active';
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <Switch
                              checked={isActive}
                              onCheckedChange={() => canManageProducts && handleToggleStatus(product)}
                              disabled={!canManageProducts}
                              data-testid={'status-toggle-' + product.id}
                            />
                            <span className={`text-[10px] font-semibold uppercase tracking-wider ${isActive ? 'text-emerald-700' : 'text-slate-500'}`}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 text-center" data-testid={'product-og-app-cell-' + product.id}>
                      {(() => {
                        const showOG = !!product.show_on_og_app;
                        const ogPrice = product.og_app_selling_price;
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <Switch
                              checked={showOG}
                              onCheckedChange={() => canManageProducts && handleToggleOGApp(product)}
                              disabled={!canManageProducts}
                              data-testid={'og-app-toggle-' + product.id}
                            />
                            <span className={`text-[10px] font-semibold uppercase tracking-wider ${showOG ? 'text-orange-700' : 'text-slate-500'}`}>
                              {showOG ? 'Published' : 'Hidden'}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">
                              {ogPrice === null || ogPrice === undefined
                                ? '— ERP price'
                                : `₹${Number(ogPrice).toFixed(2)}`}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        {product.status !== 'inactive' && product.is_active !== false && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              setGrnQuickView({ product, rows: [], loading: true });
                              try {
                                const token = localStorage.getItem('token');
                                const r = await axios.get(`${API}/products/${product.id}/grn-quickview`, { headers: { Authorization: `Bearer ${token}` } });
                                setGrnQuickView({ product, rows: r.data?.rows || [], loading: false });
                              } catch {
                                toast.error('Failed to load GRN quick view');
                                setGrnQuickView(null);
                              }
                            }}
                            title="Quick view — GRN receipts for this product"
                            data-testid={'grn-quickview-' + product.id}
                            className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                          >
                            <Eye size={16} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { window.location.href = '/inventory/batches?product_id=' + encodeURIComponent(product.id); }}
                          title="View all MRP batches for this product"
                          data-testid={'view-batches-' + product.id}
                          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                        >
                          <Layers size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const r = await axios.get(`${API}/grn/mrp-audit?product_id=${product.id}`, { headers: { Authorization: `Bearer ${token}` } });
                              setMrpHistory({ product, rows: r.data || [] });
                            } catch { toast.error('Failed to load MRP history'); }
                          }}
                          title="View MRP change history (PO MRP vs GRN MRP)"
                          data-testid={'mrp-history-' + product.id}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        >
                          <IndianRupee size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleExportHistory(product)}
                          title="Export full transaction history (PO + GRN + Invoice + DC + Audit)"
                          data-testid={'export-history-' + product.id}
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          <History size={16} />
                        </Button>
                        <CsvDownloadButton onDownload={() => handleExportHistory(product)} testId={'export-history-csv-' + product.id} className="h-8 px-2 text-xs" />
                        {canEdit('products') && (
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(product)} data-testid={'edit-product-' + product.id}><Edit size={16} /></Button>
                        )}
                        {canDelete('products') && (
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-700" data-testid={'delete-product-' + product.id}><Trash2 size={16} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredProducts.length === 0 && (
            <div className="text-center py-12 text-slate-500">No products found. Add your first product to get started.</div>
          )}
        </CardContent>
      </Card>
      <BulkUploadDialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen} onSuccess={fetchData} />
      <ProductBulkStatusDialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen} onSuccess={fetchData} />
      {mrpHistory && (
        <Dialog open onOpenChange={(o) => { if (!o) setMrpHistory(null); }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mrp-history-dialog">
            <DialogHeader>
              <DialogTitle>MRP History — {mrpHistory.product.name}</DialogTitle>
            </DialogHeader>
            {mrpHistory.rows.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No MRP changes recorded for this product</p>
            ) : (
              <table className="w-full text-xs border rounded">
                <thead><tr className="bg-slate-50 text-left">
                  <th className="p-2">Date & Time</th><th className="p-2 text-right">Old MRP</th><th className="p-2 text-right">New MRP</th>
                  <th className="p-2">PO No</th><th className="p-2 text-right">PO MRP</th>
                  <th className="p-2">GRN No</th><th className="p-2">Batch</th><th className="p-2">Changed By</th>
                </tr></thead>
                <tbody>{mrpHistory.rows.map((h) => (
                  <tr key={h.id} className="border-b" data-testid={'mrp-history-row-' + h.id}>
                    <td className="p-2">{(h.changed_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="p-2 text-right">₹{h.old_mrp}</td>
                    <td className="p-2 text-right font-semibold">₹{h.new_mrp}</td>
                    <td className="p-2 font-mono">{h.po_number || '—'}</td>
                    <td className="p-2 text-right">{h.po_mrp != null ? `₹${h.po_mrp}` : '—'}</td>
                    <td className="p-2 font-mono">{h.grn_number || '—'}</td>
                    <td className="p-2 font-mono">{h.batch_number || '—'}</td>
                    <td className="p-2">{h.changed_by_name || '—'}</td>
                  </tr>))}</tbody>
              </table>
            )}
          </DialogContent>
        </Dialog>
      )}
      {grnQuickView && (
        <Dialog open onOpenChange={(o) => { if (!o) setGrnQuickView(null); }}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="grn-quickview-dialog">
            <DialogHeader>
              <DialogTitle>GRN Quick View — {grnQuickView.product.name}</DialogTitle>
            </DialogHeader>
            {grnQuickView.loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading GRN receipts…
              </div>
            ) : grnQuickView.rows.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center" data-testid="grn-quickview-empty">No GRN receipts found for this product</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border rounded">
                  <thead><tr className="bg-slate-50 text-left">
                    <th className="p-2">GRN Number</th><th className="p-2">Invoice Number</th><th className="p-2">GRN Date</th>
                    <th className="p-2">Supplier Name</th><th className="p-2">Product ID</th><th className="p-2">Product Name</th>
                    <th className="p-2 text-right">GRN MRP</th><th className="p-2 text-right">Received Qty</th>
                    <th className="p-2 text-right">GRN Price Incl GST</th>
                  </tr></thead>
                  <tbody>{grnQuickView.rows.map((r, i) => (
                    <tr key={`${r.grn_number}-${i}`} className="border-b" data-testid="grn-quickview-row">
                      <td className="p-2 font-mono">{r.grn_number || '—'}</td>
                      <td className="p-2 font-mono">{r.invoice_number || '—'}</td>
                      <td className="p-2">{r.grn_date || '—'}</td>
                      <td className="p-2">{r.supplier_name || '—'}</td>
                      <td className="p-2 font-mono">{r.product_code || '—'}</td>
                      <td className="p-2">{r.product_name || '—'}</td>
                      <td className="p-2 text-right">{r.grn_mrp != null ? `₹${Number(r.grn_mrp).toFixed(2)}` : '—'}</td>
                      <td className="p-2 text-right">{r.received_qty}</td>
                      <td className="p-2 text-right">{r.grn_price_incl_gst ? `₹${Number(r.grn_price_incl_gst).toFixed(2)}` : '—'}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProductsEnhanced;
